import React, { useContext, useMemo, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { Add } from '@mui/icons-material'
import { CircularProgress, Tooltip } from '@mui/material'
import { useSelector } from 'react-redux'
import { getAssessmentResultsTrackingFetch, getAssessmentExecutionFetch, getStudentImprovementTrackingFetch, getLearningCenterBreakdownFetch } from 'store/middlewares/assessment.crud'
import { AssessmentResultsTrackingContext, orderBtns, profileBtns } from '../AssessmentResultsTracking/AssessmentResultsTrackingContext'
import { AffiliationsHeader, BarLine, assessmentResultsTrackingHooks } from '../AssessmentResultsTracking'
import { SystemReviewContext } from '../SystemReviewContext'
import NumberAxis from '../NumberAxis'
import { BarLineStyles } from '../Style'
import { semesterAndYearOptions } from 'keys/years'
import { semesters } from 'keys'
import qc from 'keys/queryClient'
import { AchievementGraphStyles } from './Style'
import SelectCombinations from './SelectCombinations'
import { AutoComplete, IconButtons } from 'components/Form'
import { IconButtonGroups } from 'styles/GlobalStyle'
import { RatingChips } from 'components/Rating'
import { secretStorage } from 'config'
import { affiliationGroupNames } from 'keys/affiliationGroups'
import { keyBy } from 'lodash'

const DEFAULT_YEARS = [semesterAndYearOptions[semesterAndYearOptions.length - 1]]
const DEFAULT_SEMESTERS = [semesters[0], semesters[1], semesters[2]]

const compareOptions = [
    { id: 'students', name: 'נתוני שיפור תלמידים' },
    { id: 'institutions', name: 'נתוני מוסדות לימוד' }
]

const segmentationOptions = [
    { id: 'gender', name: 'בנים + בנות' },
    { id: 'immigrants', name: 'עולים + ישראלים' },
]

const AchievementGraph = () => {
    const { appliedAssessmentFilters } = useContext(SystemReviewContext)
    const { brokenDisplay } = useSelector(state => state.general)
    const { roleIndex } = useSelector(state => state.user)
    const { grades } = useSelector(state => state.testTaker)

    const storage = secretStorage.getItem("user")
    const { user_data } = storage || {}
    const { affiliations, affiliation_groups } = user_data?.[roleIndex]?.user_data || {}
    const filterAffiliationGroups = affiliation_groups?.filter(group => affiliationGroupNames.includes(group.name))

    const [appliedYears, setAppliedYears] = useState(DEFAULT_YEARS)
    const [appliedSemesters, setAppliedSemesters] = useState(DEFAULT_SEMESTERS)
    const [compareType, setCompareType] = useState(compareOptions[0])
    const [order, setOrder] = useState(orderBtns[0].key)
    const [profileType, setProfileType] = useState(profileBtns[0].key)
    const [segmentation, setSegmentation] = useState(null)
    const [layout, setLayout] = useState(null)

    const { data: cities = [] } = useQuery({ queryKey: [qc.cities] })

    const segmentTypeOptions = assessmentResultsTrackingHooks.useSegmentTypeOptions()

    const isStudentsMode = compareType?.id === 'students'  // ← שנה ל-optional chaining (?.)

    // ─── Institutions mode ────────────────────────────────────────────────────

    const combinations = useMemo(() => {
        if (isStudentsMode) return []
        return appliedYears.flatMap(year =>
            appliedSemesters.map(sem => ({ school_year: year, semester: sem }))
        ).sort((a, b) => {
            if (a.school_year.id !== b.school_year.id) return a.school_year.id - b.school_year.id
            return Number(a.semester.id) - Number(b.semester.id)
        })
    }, [appliedYears, appliedSemesters, isStudentsMode])

    const institutionQueries = useQueries({
        queries: combinations.map(combo => ({
            queryKey: [qc.assessmentResultsTrackingByAffiliations, 'progress-inst', combo, appliedAssessmentFilters, layout],
            queryFn: async ({ signal }) => {
                const { data } = await getAssessmentResultsTrackingFetch(
                    { ...appliedAssessmentFilters, semester_and_year: combo, layout },
                    'results', 0, signal
                )
                return { schools: data, combo }
            },
            enabled: combinations.length > 0
        }))
    })

    const executionQueries = useQueries({
        queries: combinations.map(combo => ({
            queryKey: [qc.assessmentResultsTrackingByAffiliations, 'execution', combo, appliedAssessmentFilters],
            queryFn: async ({ signal }) => {
                const { data } = await getAssessmentExecutionFetch(
                    { ...appliedAssessmentFilters, semester_and_year: combo },
                    signal
                )
                return { schools: data, combo }
            },
            enabled: combinations.length > 0
        }))
    })

    // פירוט מרכזי למידה לטולטיפ — קריאות קלות שרצות במקביל לקריאות הראשיות
    const lcBreakdownQueries = useQueries({
        queries: combinations.map(combo => ({
            queryKey: [qc.learningCenterBreakdown, 'progress-inst', combo, appliedAssessmentFilters, layout],
            queryFn: async ({ signal }) => {
                const { data } = await getLearningCenterBreakdownFetch(
                    { ...appliedAssessmentFilters, semester_and_year: combo },
                    layout, null, signal
                )
                return { breakdown: data, combo }
            },
            enabled: combinations.length > 0
        }))
    })

    const lcBreakdownByCombo = useMemo(() => {
        const map = {}
        combinations.forEach((combo, idx) => {
            const result = lcBreakdownQueries[idx]?.data
            if (!result) return
            map[`${combo.school_year.id}_${combo.semester.id}`] = result.breakdown
        })
        return map
    }, [lcBreakdownQueries, combinations])

    const executionBySchool = useMemo(() => {
        const map = {}
        combinations.forEach((combo, idx) => {
            const result = executionQueries[idx]?.data
            if (!result?.schools) return
            const comboKey = `${combo.school_year.id}_${combo.semester.id}`
            result.schools.forEach(entity => {
                if (!map[entity.id]) map[entity.id] = {}
                map[entity.id][comboKey] = entity.test_taker_execution_count
            })
        })
        return map
    }, [executionQueries, combinations])

    const institutionData = useMemo(() => {
        const map = {}
        combinations.forEach((combo, idx) => {
            const result = institutionQueries[idx]?.data
            if (!result?.schools) return
            result.schools.forEach(entity => {
                if (!map[entity.id]) {
                    map[entity.id] = { id: entity.id, name: entity.name, combos: Array(combinations.length).fill(null) }
                }
                map[entity.id].combos[idx] = {
                    ...entity,
                    comboKey: `${combo.school_year.id}_${combo.semester.id}`,
                    semesterLabel: combo.semester.name
                }
            })
        })
        return Object.values(map)
            .filter(entity => entity.combos.some(c => (c?.test_taker_execution_count ?? 0) > 0))
            .map(entity => ({
                ...entity,
                combos: entity.combos.map((c, i) => c ?? {
                    id: entity.id, name: entity.name,
                    comboKey: `${combinations[i].school_year.id}_${combinations[i].semester.id}`,
                    semesterLabel: combinations[i].semester.name,
                    test_taker_execution_count: 0, test_taker_count: 0,
                    expected_percent: 0, expected_count: 0,
                    rating_1: 0, rating_2: 0, rating_3: 0
                })
            }))
            .sort((a, b) => {
                if (order === 'name') return a.name.localeCompare(b.name, 'he')
                const maxA = Math.max(...a.combos.map(c => c.expected_percent || 0))
                const maxB = Math.max(...b.combos.map(c => c.expected_percent || 0))
                return maxB - maxA
            })
    }, [institutionQueries, combinations, order])

    // ─── Students mode ────────────────────────────────────────────────────────

    const improvementQueries = useQueries({
        queries: isStudentsMode ? appliedYears.map(year => ({
            queryKey: [qc.assessmentResultsTrackingByAffiliations, 'improvement', year, appliedAssessmentFilters, layout],
            queryFn: async ({ signal }) => {
                const { data } = await getStudentImprovementTrackingFetch(
                    { ...appliedAssessmentFilters, semester_and_year: { school_year: year, semester: null } },
                    layout,
                    signal
                )
                return { result: data, year }
            },
        })) : []
    })

    // פירוט מרכזי למידה לטולטיפ במצב מבדק ראשון/אחרון — במקביל לקריאה הראשית
    const improvementLcQueries = useQueries({
        queries: isStudentsMode ? appliedYears.map(year => ({
            queryKey: [qc.learningCenterBreakdown, 'improvement', year, appliedAssessmentFilters, layout],
            queryFn: async ({ signal }) => {
                const { data } = await getLearningCenterBreakdownFetch(
                    { ...appliedAssessmentFilters, semester_and_year: { school_year: year, semester: null } },
                    layout, 'improvement', signal
                )
                return { breakdown: data, year }
            },
        })) : []
    })

    const improvementLcByYear = useMemo(() => {
        const map = {}
        appliedYears.forEach((year, idx) => {
            const result = improvementLcQueries[idx]?.data
            if (result) map[year.id] = result.breakdown // { first: {...}, last: {...} }
        })
        return map
    }, [improvementLcQueries, appliedYears])

    const improvementData = useMemo(() => {
        if (!isStudentsMode) return []
        const allEntities = {}

        appliedYears.forEach((year, idx) => {
            const q = improvementQueries[idx]
            if (!q?.data?.result) return
            const { first, last } = q.data.result
            const lastById = keyBy(last, 'id')

            first.forEach(firstEntity => {
                const lastEntity = lastById[firstEntity.id]
                if (!lastEntity) return
                if (!allEntities[firstEntity.id]) {
                    allEntities[firstEntity.id] = { id: firstEntity.id, name: firstEntity.name, slots: [] }
                }
                allEntities[firstEntity.id].slots.push({ year, firstEntity, lastEntity })
            })
        })

        return Object.values(allEntities)
            .map(entity => {
                const firstPercent = entity.slots[0]?.firstEntity.expected_percent || 0
                const lastPercent = entity.slots[entity.slots.length - 1]?.lastEntity.expected_percent || 0
                const diff = Math.round(Math.abs(lastPercent - firstPercent) * 100)
                const isPositive = lastPercent >= firstPercent
                return {
                    ...entity,
                    improvement: diff ? { text: isPositive ? `שיפור של ${diff}% בין הסמסטרים` : "", isPositive } : null
                    // Change to next line to show also  negative comment
                    // improvement: diff ? {text:  `שיפור של ${diff}% בין הסמסטרים` , isPositive} : null
                }
            })
            .sort((a, b) => {
                if (order === 'name') return a.name.localeCompare(b.name, 'he')
                const lastPctA = a.slots[a.slots.length - 1]?.lastEntity.expected_percent || 0
                const lastPctB = b.slots[b.slots.length - 1]?.lastEntity.expected_percent || 0
                return lastPctB - lastPctA
            })
    }, [improvementQueries, appliedYears, isStudentsMode, order])

    // ─── Shared ───────────────────────────────────────────────────────────────

    const totalTestTakers = useMemo(() => {
        if (isStudentsMode) {
            return improvementData.reduce((sum, e) => {
                const lastSlot = e.slots[e.slots.length - 1]
                return sum + (lastSlot?.lastEntity.test_taker_execution_count || 0)
            }, 0)
        }
        if (!institutionData.length || !combinations.length) return 0
        const lastIdx = combinations.length - 1
        return institutionData.reduce((sum, entity) => sum + (entity.combos[lastIdx]?.test_taker_execution_count || 0), 0)
    }, [isStudentsMode, improvementData, institutionData, combinations])

    const contextValue = useMemo(() => ({
        profileType, setProfileType,
        layout, setLayout,
        segmentation, setSegmentation,
        totalTestTakers,
        affiliations,
        affiliationGroups: filterAffiliationGroups,
        grades,
        cities,
        semesterAndYear: null,
        setSemesterAndYear: () => { },
    }), [profileType, layout, segmentation, totalTestTakers, affiliations, filterAffiliationGroups, grades, cities])

    const isInstitutionLoading = !isStudentsMode && institutionQueries.some(q => q.isLoading) && combinations.length > 0
    const isStudentsLoading = isStudentsMode && improvementQueries.some(q => q.isLoading) && appliedYears.length > 0

    const getInstitutionImprovement = (combos) => {
        const withData = combos.filter(c => (c.test_taker_execution_count ?? 0) > 0)
        if (withData.length < 2) return null
        const first = withData[0].expected_percent || 0
        const last = withData[withData.length - 1].expected_percent || 0
        const diff = Math.round(Math.abs(last - first) * 100)
        if (!diff) return null
        return { text: `שיפור של ${diff}% בין הסמסטרים`, isPositive: last >= first }
    }

    const semesterControl = (
        <SelectCombinations
            years={appliedYears}
            semesters={appliedSemesters}
            yearOnly={isStudentsMode}
            onApply={(years, sems) => { setAppliedYears(years); setAppliedSemesters(sems) }}
        />
    )

    const renderStudentsContent = () => {
        if (appliedYears.length === 0)
            return <div className='affiliations-loading'>{"בחר שנה להצגת נתוני שיפור תלמידים"}</div>
        if (isStudentsLoading)
            return <div className='affiliations-loading'><CircularProgress color="thirdly" /></div>
        if (improvementData.length === 0)
            return <div className='affiliations-loading'>{"אין נתונים לסינון זה"}</div>

        return improvementData.map(entity =>
            entity.slots.map(({ year, firstEntity, lastEntity }) => (
                <div key={`${entity.id}_${year.id}`} className='bar-item-and-label progress-school-group'>
                    <div className='left-section progress-school-info'>
                        <div className={`layout-label ${brokenDisplay ? 'blurred2' : ''}`}>
                            {entity.name}
                        </div>
                        {entity.improvement && (
                            <div className={`progress-improvement ${entity.improvement.isPositive ? 'positive' : 'negative'}`}>
                                {entity.improvement.text}
                            </div>
                        )}
                    </div>
                    <div className='progress-school-bars'>
                        {[
                            { label: 'מבדק ראשון', data: firstEntity, phase: 'first' },
                            { label: 'מבדק אחרון', data: lastEntity, phase: 'last' },
                        ].map(({ label, data, phase }) => (
                            <div key={label} className='progress-combo-row'>
                                <div className='progress-semester-tag'>
                                    <span className='progress-semester-name'>{label}</span>
                                    <Tooltip
                                        title={data.test_taker_execution_count
                                            ? `${data.test_taker_execution_count} תלמידים`
                                            : 'אין נתונים'}
                                        placement="top"
                                    >
                                        <span>
                                            <i className="fa-thin fa-users semester-info-icon" />
                                        </span>
                                    </Tooltip>
                                    <i className="fa-thin fa-arrow-left-long chevron-icon" />
                                </div>
                                {data.test_taker_execution_count > 0
                                    ? <BarLine
                                        {...data}
                                        showBarSummary={false}
                                        lcBreakdown={improvementLcByYear[year.id]
                                            ? (improvementLcByYear[year.id][phase]?.[entity.id] || {})
                                            : undefined}
                                    />
                                    : <div className='progress-no-data'>{"אין נתונים"}</div>
                                }
                            </div>
                        ))}
                    </div>
                </div>
            ))
        )
    }

    const renderInstitutionsContent = () => {
        if (combinations.length === 0)
            return <div className='affiliations-loading'>{"בחר שנה וסמסטרים להצגת נתוני התקדמות"}</div>
        if (isInstitutionLoading)
            return <div className='affiliations-loading'><CircularProgress color="thirdly" /></div>
        if (institutionData.length === 0)
            return <div className='affiliations-loading'>{"אין נתונים לסינון זה"}</div>

        return institutionData.map(entity => {
            const improvement = getInstitutionImprovement(entity.combos)
            return (
                <div key={entity.id} className='bar-item-and-label progress-school-group'>
                    <div className='left-section progress-school-info'>
                        <div className={`layout-label ${brokenDisplay ? 'blurred2' : ''}`}>
                            {entity.name}
                        </div>
                        {improvement && (
                            <div className={`progress-improvement ${improvement.isPositive ? 'positive' : 'negative'}`}>
                                {//delete improvement.isPositive &&  to shoe negative comment
                                }
                                {improvement.isPositive && improvement.text}
                            </div>
                        )}
                    </div>
                    <div className='progress-school-bars'>
                        {entity.combos.map(combo => {
                            const totalExec = executionBySchool[entity.id]?.[combo.comboKey]
                            return (
                                <div key={combo.comboKey} className='progress-combo-row'>
                                    <div className='progress-semester-tag'>
                                        <span className='progress-semester-name'>{combo.semesterLabel}</span>
                                        <Tooltip
                                            title={combo.test_taker_execution_count
                                                ? `${combo.test_taker_execution_count} תלמידים מתוך ${totalExec ?? '...'}`
                                                : 'אין נתונים'}
                                            placement="top"
                                        >
                                            <span>
                                                <i className="fa-thin fa-users semester-info-icon" />
                                            </span>
                                        </Tooltip>
                                        <i className="fa-thin fa-arrow-left-long chevron-icon" />
                                    </div>
                                    {combo.test_taker_execution_count > 0
                                        ? <BarLine
                                            {...combo}
                                            showBarSummary={false}
                                            lcBreakdown={lcBreakdownByCombo[combo.comboKey]
                                                ? (lcBreakdownByCombo[combo.comboKey][entity.id] || {})
                                                : undefined}
                                        />
                                        : <div className='progress-no-data'>{"אין נתונים"}</div>
                                    }
                                </div>
                            )
                        })}
                    </div>
                </div>
            )
        })
    }

    return (
        <AssessmentResultsTrackingContext.Provider value={contextValue}>
            <AchievementGraphStyles>
                <div className='filters-header'>
                    <AffiliationsHeader
                        description={" מוצגים נתוני הקריאה של "}
                        Context={AssessmentResultsTrackingContext}
                        semesterControl={semesterControl}
                    />
                </div>
                <BarLineStyles sx={{ border: '1px solid #E3E8F0' }}>
                    <div className='bar-lines-header'>
                        <div className='bar-line-filters'>
                            <div className='compare-label'>{"בהשוואה בין"}</div>
                            <div className='compare-label-group'>
                                <AutoComplete
                                    width={220}
                                    minWidth={220}
                                    placeholder="בחר סוג השוואה"
                                    options={compareOptions}
                                    input={{
                                        value: compareType || compareOptions[0],  // ✅ Default לאפשרות הראשונה
                                        onChange: setCompareType
                                    }}
                                />
                                <IconButtonGroups>
                                    <IconButtons
                                        btns={orderBtns}
                                        setSelected={(e, val) => setOrder(val)}
                                        selected={order}
                                    />
                                </IconButtonGroups>
                            </div>
                        </div>
                        <div className='bar-line-filters'>
                            <div className='flex'>
                                <div className='left-section'>
                                    <AutoComplete
                                        minWidth={180}
                                        width={180}
                                        placeholder="בחר השוואה"
                                        options={segmentTypeOptions}
                                        input={{ value: layout, onChange: setLayout }}
                                    />
                                </div>
                                <div className='select-segmentation'>
                                    <AutoComplete
                                        fullWidth
                                        name="segmentation"
                                        input={{ value: segmentation, onChange: setSegmentation }}
                                        popupIcon={<Add fontSize="small" />}
                                        placeholder="הוספת פילוח"
                                        options={segmentationOptions}
                                    />
                                </div>
                            </div>
                            <div className='profiles'>
                                <RatingChips type={profileType} />
                                <IconButtonGroups>
                                    <IconButtons
                                        btns={profileBtns}
                                        setSelected={(e, val) => setProfileType(val)}
                                        selected={profileType}
                                    />
                                </IconButtonGroups>
                            </div>
                        </div>
                    </div>

                    <div className='bar-line-container'>
                        {isStudentsMode ? renderStudentsContent() : renderInstitutionsContent()}
                    </div>
                </BarLineStyles>

                <NumberAxis />
            </AchievementGraphStyles>
        </AssessmentResultsTrackingContext.Provider>
    )
}

export default AchievementGraph
